import { useCallback, UIEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, ScrollShadow } from '@ds';
import { FaArrowLeft } from 'react-icons/fa';
import { Notification } from './components/Notification';
import { useInfiniteNotificationsListQuery } from '@/queries/notification';
import { NotificationType } from '@/api/apis/communication';

export const NotificationsHub = () => {
  const navigate = useNavigate();
  const { data, isLoading, isFetchingNextPage, isError, fetchNextPage, hasNextPage } =
    useInfiniteNotificationsListQuery({});
  const notifications: { [key: string]: NotificationType[] } = data?.items || {};
  const keys = data?.sortedKeys || [];
  const hasScrollHandler = !!fetchNextPage && hasNextPage;
  const isFetching = isLoading || isFetchingNextPage;

  const handleScroll = useCallback(
    ({ currentTarget }: UIEvent<HTMLDivElement>) => {
      if (!hasScrollHandler || isFetching) return;

      const isScrolledToBottom = currentTarget.scrollHeight - currentTarget.scrollTop <= currentTarget.scrollWidth;

      if (isScrolledToBottom) {
        fetchNextPage();
      }
    },
    [hasScrollHandler, fetchNextPage, isFetching],
  );

  if (isFetching && keys.length === 0) {
    return <div>Loading...</div>;
  }

  if (isError) {
    return <div>Error loading notifications</div>;
  }

  return (
    <div className="flex flex-col items-start p-4 bg-gray-100 h-full gap-4 overflow-hidden">
      <div className="flex flex-col gap-2 w-full items-start">
        <div>
          <Button className="p-1" size="lg" variant="light" onClick={() => navigate(-1)}>
            <FaArrowLeft />
            <span className="text-m-sb text-base-5">Back to list</span>
          </Button>
        </div>
        <h1 className="text-h-l text-base-5">Notifications</h1>
      </div>

      <ScrollShadow size={10} className="w-full h-full overflow-auto" onScroll={handleScroll}>
        <div className="flex flex-col items-center py-1 w-full">
          {keys.map((key) => (
            <div key={key} className="w-full flex flex-col gap-2 items-center">
              {key !== 'Today' && (
                <div className="flex items-center w-full mt-4 mb-2">
                  <hr className="grow border-t border-base-50" />
                  <div className="flex bg-white h-6 rounded-xl border border-base-50 items-center justify-center px-3 py-1">
                    <span className="text-s-m text-base-20">{key}</span>
                  </div>
                  <hr className="grow border-t border-base-50" />
                </div>
              )}
              {notifications[key]?.map((notification) => (
                <Notification key={notification.id} notification={notification} />
              ))}
            </div>
          ))}
          {isFetching && <div>Loading more...</div>}
        </div>
      </ScrollShadow>
    </div>
  );
};
